import { apiClient } from '@lib/api/client';
import type { CardInfo } from '../types/subscription.types';

export interface BundlePurchaseResponse {
  success: boolean;
  payment_completed: boolean;
  message: string;
  certificate_subscription_id: string;
  /** La suscripción del certificado quedó activa. */
  certificate_active: boolean;
  /** El registro del certificado quedó marcado como pagado. */
  certificate_registered: boolean;
  /** El plan de Factura Libre quedó activado (cobrado dentro del bundle). */
  plan_activated: boolean;
  plan_subscription_id: string | null;
  transaction_id: string | null;
  amount: number;
}

export interface CertificateBundleRegistrationResponse {
  message: string;
  data: {
    id: string;
    email: string;
    razonSocial: string;
  };
}

export interface BundlePrefill {
  company: {
    razonSocial: string;
    ruc: string;
    dv: string;
    email: string;
    telefono: string;
    tipoPersona: 'NATURAL' | 'JURIDICA';
  };
  ubicacion: { codigoUbicacion?: string; disponible: boolean };
  /** Campos que el portal no pudo derivar y el formulario debe pedir. */
  faltantes: string[];
}

export const certificateBundleService = {
  /** Datos de la empresa que ya tenemos, para no volver a pedirlos. */
  async getPrefill(): Promise<BundlePrefill> {
    const response = await apiClient.get<BundlePrefill>('/certificates/bundle/prefill');
    return response.data;
  },

  async registerBundle(payload: {
    fullName: string;
    telefono?: string;
    avisoOperacion: File;
  }): Promise<CertificateBundleRegistrationResponse> {
    const formData = new FormData();
    formData.append('fullName', payload.fullName);
    formData.append('aceptaTerminos', 'true');
    if (payload.telefono) formData.append('telefono', payload.telefono);
    formData.append('avisoOperacion', payload.avisoOperacion);

    // El apiClient trae 'application/json' por defecto, que rompe el boundary de
    // multipart y deja al backend sin archivo. Igual que el resto de los uploads
    // del portal, se declara explícitamente.
    const response = await apiClient.post('/certificates/bundle/registro', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  },

  /**
   * Compra el bundle: un solo cobro contra la suscripción del certificado, y el
   * plan se cobra dentro del mismo cargo.
   *
   * No se envía monto: lo fija el servidor. El navegador solo identifica qué
   * solicitud de certificado y qué suscripción de plan corresponden.
   */
  async purchase(payload: {
    bundleRegistroId: string;
    planSubscriptionId: string;
    /** Plan a activar. El backend verifica contra el catálogo que admita bundle. */
    planId: string;
    cardInfo: CardInfo;
    customerName?: string;
    customerPhone?: string;
    customerAddress?: string;
  }): Promise<BundlePurchaseResponse> {
    const response = await apiClient.post('/companies/purchase-certificate-bundle', {
      ...payload,
      // Evita que un doble click cobre dos veces.
      idempotencyKey: crypto.randomUUID(),
    });
    return response.data;
  },

  // No hay updateBundle a propósito: marcar el certificado como pagado lo hace
  // el backend dentro de purchase(), tras confirmarse el cobro. Desde el
  // navegador no se puede (ni se debe).
};
